import type { ICanonicalChildWorkOrder, ICanonicalParentWorkOrder, ICanonicalWorkOrderBase } from '@atlas/types'
import { EmptyState, ListSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatUtc } from '@/lib/format-utc'
import type { BundleCanonicalDTO } from '@/services/workfront-mappings.service'
import { humanize } from '../../components/StatusBadges'

/** What the page knows of an error a hold names: its title, and where its fix is. */
export interface HoldError {
	title: string
	fixLink: string | null
}

export interface CanonicalViewProps {
	record: BundleCanonicalDTO | null
	isLoading?: boolean
	/** The bundle's open errors by ID, to name the errors that hold an object. */
	errorsById: Record<string, HoldError>
	/** The bundle's objects by Workfront ID → their name, to name held objects. */
	namesById?: Record<string, string>
	remapping?: boolean
	onRemap: () => void
}

function Line({ label, value }: { label: string; value: ReactNode }) {
	if (value === null || value === undefined || value === '') return null
	return (
		<div className="flex gap-2 text-ds-sm">
			<dt className="w-40 shrink-0 text-ds-text-muted">{label}</dt>
			<dd>{value}</dd>
		</div>
	)
}

const tupleLine = (tuple: ICanonicalWorkOrderBase['tuple']) =>
	Object.entries(tuple)
		.filter(([, codes]) => codes.length)
		.map(([axis, codes]) => `${humanize(axis)}: ${codes.join(', ')}`)
		.join(' · ')

const money = (value: { amountMinor: number; currency: string } | null) =>
	value ? `${(value.amountMinor / 100).toFixed(2)} ${value.currency}` : null

/** The parts a parent and a child share: tuple, due date, description, tasks, comments and documents. */
function WorkOrderParts({ order }: { order: ICanonicalWorkOrderBase }) {
	return (
		<>
			<Line label="Reference" value={order.external.referenceNumber ? `#${order.external.referenceNumber}` : null} />
			<Line label="Status" value={order.external.statusLabel ?? order.external.status} />
			<Line label="Due" value={order.dueAt ? formatUtc(order.dueAt) : null} />
			<Line label="Classification" value={tupleLine(order.tuple)} />
			<Line label="Description" value={order.description.map((section) => section.heading).join(', ')} />
			<Line
				label="Tasks"
				value={order.tasks.map((task) => `${task.name}${task.stage ? ` (${task.stage})` : ''}`).join(', ')}
			/>
			<Line label="Comments" value={order.comments.length || null} />
			<Line label="Documents" value={order.documents.map((document) => document.name).join(', ')} />
		</>
	)
}

function ParentCard({ parent }: { parent: ICanonicalParentWorkOrder }) {
	return (
		<section aria-label="Parent" className="rounded-ds-sm border border-ds-border p-3">
			<h3 className="font-medium">{parent.title}</h3>
			<dl className="mt-2 space-y-1">
				<WorkOrderParts order={parent} />
				<Line label="Owner" value={parent.owner?.name} />
				<Line label="Budget" value={money(parent.budget)} />
				<Line label="Planned assets" value={parent.plannedAssetCount} />
				<Line label="PM region" value={parent.pmRegion} />
			</dl>
		</section>
	)
}

function ChildCard({ child }: { child: ICanonicalChildWorkOrder }) {
	return (
		<li className="rounded-ds-sm border border-ds-border p-3">
			<h4 className="font-medium">{child.title}</h4>
			<dl className="mt-2 space-y-1">
				<WorkOrderParts order={child} />
				<Line label="Asset type" value={child.assetTypeCode} />
				<Line label="Complexity" value={child.complexityLevel} />
				<Line label="Campaign" value={child.campaign?.name} />
			</dl>
		</li>
	)
}

/** The held objects, each with the errors that hold it and a Fix where one is known. */
function Holds({
	record,
	errorsById,
	namesById = {},
}: Pick<CanonicalViewProps, 'errorsById' | 'namesById'> & { record: BundleCanonicalDTO }) {
	if (!record.held.length) return null
	return (
		<section>
			<h3 className="font-medium">Held ({record.held.length})</h3>
			<ul aria-label="Held objects" className="space-y-2 text-ds-sm">
				{record.held.map((hold) => (
					<li key={hold.workfrontId}>
						<span className="font-medium">
							{humanize(hold.role)} {namesById[hold.workfrontId] ?? hold.workfrontId}
						</span>
						<ul className="ml-4">
							{hold.errorIds.map((errorId, index) => {
								const error = errorsById[errorId]
								return (
									<li key={errorId} className="flex flex-wrap items-center gap-2">
										<span>{error?.title ?? hold.codes[index] ?? errorId}</span>
										{error?.fixLink ? (
											<Link className="font-medium underline" to={error.fixLink}>
												Fix
											</Link>
										) : null}
									</li>
								)
							})}
						</ul>
					</li>
				))}
			</ul>
		</section>
	)
}

function Warnings({ record }: { record: BundleCanonicalDTO }) {
	if (!record.warnings.length) return null
	return (
		<section>
			<h3 className="font-medium">Warnings ({record.warnings.length})</h3>
			<ul aria-label="Warnings" className="text-ds-sm">
				{record.warnings.map((warning) => (
					<li key={`${warning.workfrontId}:${warning.code}`}>
						{warning.code} on {humanize(warning.role)} {warning.workfrontId}
					</li>
				))}
			</ul>
		</section>
	)
}

/**
 * A bundle's latest canonical record, as the mapper built it: the parent and each
 * child in readable form (or the JSON), what is held and why with a Fix for each,
 * and the warnings. Re-map asks for the bundle to be mapped again.
 */
export function CanonicalView({ record, isLoading, errorsById, namesById, remapping, onRemap }: CanonicalViewProps) {
	const [json, setJson] = useState(false)
	const remap = (
		<Button size="sm" variant="outline" isDisabled={remapping} onPress={onRemap}>
			{remapping ? 'Re-mapping…' : 'Re-map'}
		</Button>
	)
	if (isLoading) return <ListSkeleton rows={3} label="Loading the canonical record" />
	if (!record)
		return <EmptyState title="Not mapped yet" description="This bundle has no canonical record yet." action={remap} />
	const { canonical } = record
	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<p className="text-ds-sm text-ds-text-muted">
					Bundle version {record.version} · mapping version {record.mappingVersion} · mapped{' '}
					{formatUtc(record.mappedAt)}
				</p>
				<div className="flex gap-2">
					<Button size="sm" variant="ghost" aria-pressed={json} onPress={() => setJson(!json)}>
						{json ? 'Readable form' : 'JSON'}
					</Button>
					{remap}
				</div>
			</div>
			{json ? (
				<pre aria-label="Canonical JSON" className="overflow-x-auto rounded-ds-sm bg-ds-surface p-3 text-ds-xs">
					{JSON.stringify(canonical, null, 2)}
				</pre>
			) : (
				<>
					{canonical.parent ? (
						<ParentCard parent={canonical.parent} />
					) : (
						<p className="text-ds-sm text-ds-danger">The parent is held: nothing in this bundle is applied.</p>
					)}
					{canonical.children.length ? (
						<ul aria-label="Children" className="space-y-2">
							{canonical.children.map((child) => (
								<ChildCard key={child.external.ref} child={child} />
							))}
						</ul>
					) : null}
				</>
			)}
			<Holds record={record} errorsById={errorsById} namesById={namesById} />
			<Warnings record={record} />
		</div>
	)
}
